/**
 * @fileoverview useChatVoiceInput — thin wrapper around
 * `useRealtimeTranscription` for chat composers.
 *
 * Purpose:
 * - Lets any chat input offer voice dictation with the same engine the
 *   notes editor uses (OpenAI Realtime over WebSocket — see
 *   `use-realtime-transcription.ts`).
 * - Accumulates streamed interim deltas into a single `interim` string
 *   for live display, surfaces a `toggle()` for the mic/stop button,
 *   and hands the cleaned final transcript back via `onFinal`.
 * - Routes transcription errors to a toast so consumers don't each
 *   have to.
 *
 * Used by:
 * - `ChatInput` (the shared composer behind ~11 chat surfaces)
 * - `RefineNarrativeSheet` (bank narrative refinement chat)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  useRealtimeTranscription,
  type TranscriptionState,
} from "@/ui/hooks/use-realtime-transcription";

export interface UseChatVoiceInputOptions {
  /**
   * Fires with the cleaned final transcript when a dictation session
   * ends. Consumers append/insert this into their input value.
   */
  onFinal: (transcript: string) => void;
}

export interface ChatVoiceInput {
  /** Underlying session state: idle / listening / processing. */
  state: TranscriptionState;
  /** Live, accumulated interim transcript for the current session. */
  interim: string;
  /** Mic is open and audio is streaming. */
  isListening: boolean;
  /** User stopped; waiting for the server's final transcript. */
  isProcessing: boolean;
  /** True while listening or processing (i.e. not idle). */
  isActive: boolean;
  /** Start if idle, stop if listening. No-op while processing. */
  toggle: () => void;
  /** False when this deployment has dictation off: hide the mic control. */
  isEnabled: boolean;
}

/**
 * Joins a transcript fragment onto existing text, inserting a single
 * space only when needed so dictated words don't run into typed text
 * (or stack double spaces after punctuation/whitespace).
 */
export function appendTranscript(existing: string, addition: string): string {
  const trimmedAddition = addition.trim();
  if (trimmedAddition.length === 0) return existing;
  if (existing.length === 0) return trimmedAddition;
  const needsSpace = !/\s$/.test(existing);
  return `${existing}${needsSpace ? " " : ""}${trimmedAddition}`;
}

/**
 * Whether a dictation control (mic or stop) should render. The deployment
 * offers dictation (`isEnabled`), or a session is already open: a mic that
 * is streaming keeps its stop control even if the flag turns off mid-session
 * (the hook does not end a live session when the flag changes).
 */
export function isDictationControlShown(
  voice: Pick<ChatVoiceInput, "isEnabled" | "isListening" | "isProcessing">,
): boolean {
  return voice.isEnabled || voice.isListening || voice.isProcessing;
}

export function useChatVoiceInput({
  onFinal,
}: UseChatVoiceInputOptions): ChatVoiceInput {
  const interimRef = useRef<string>("");
  const [interim, setInterim] = useState<string>("");

  // Keep the latest onFinal in a ref so changing it between renders
  // doesn't tear down the live transcription session.
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  const transcription = useRealtimeTranscription({
    onDelta: (delta) => {
      interimRef.current = `${interimRef.current}${delta}`;
      setInterim(interimRef.current);
    },
    onFinal: (final) => {
      interimRef.current = "";
      setInterim("");
      onFinalRef.current(final);
    },
    onError: (message) => {
      toast.error(message);
    },
  });

  const isListening = transcription.state === "listening";
  const isProcessing = transcription.state === "processing";

  // Clear any leftover interim once the session fully ends.
  useEffect(() => {
    if (transcription.state === "idle") {
      interimRef.current = "";
      setInterim("");
    }
  }, [transcription.state]);

  const toggle = useCallback(() => {
    if (isListening) {
      transcription.stop();
    } else if (!isProcessing) {
      void transcription.start();
    }
  }, [isListening, isProcessing, transcription]);

  return {
    state: transcription.state,
    interim,
    isListening,
    isProcessing,
    isActive: transcription.state !== "idle",
    toggle,
    isEnabled: transcription.isEnabled,
  };
}
